/**
 * RecentTabsSection Component
 *
 * Displays a searchable list of open and recently closed tabs.
 * Supports keyboard navigation and fuzzy search filtering.
 */

import { useState, useCallback, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import { ChevronDown, Search, Layers } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { cn } from '../../../../lib/utils';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useTabHistoryStore } from '../../../tab-history/store/tabHistoryStore';
import { tabHistoryEntryToQueryContent } from '../../../tab-history/utils/tabHistoryReopen';
import { TabListItem } from './TabListItem';
import { EmptyState } from './EmptyState';
import { AnimatedCollapse } from './AnimatedCollapse';
import {
  tabToRecentItem,
  historyEntryToRecentItem,
  filterTabItems,
  sortTabItems,
  type RecentTabItem,
} from '../utils/tabFiltering';
import type { TabHistoryEntry } from '../../../tab-history/types/tabHistoryTypes';

type RecentTabsSectionProps = {
  /** Whether the section is expanded */
  isExpanded: boolean;
  /** Callback when section expand/collapse state changes */
  onExpandedChange: (expanded: boolean) => void;
  /** Current search query */
  searchQuery: string;
  /** Callback when search query changes */
  onSearchQueryChange: (query: string) => void;
};

export function RecentTabsSection({
  isExpanded,
  onExpandedChange,
  searchQuery,
  onSearchQueryChange,
}: RecentTabsSectionProps) {
  // Workspace store state
  const tabs = useWorkspaceStore((state) => state.tabs);
  const setActiveTab = useWorkspaceStore((state) => state.setActiveTab);
  const addTab = useWorkspaceStore((state) => state.addTab);

  // Tab history store state
  const closedTabsBySession = useTabHistoryStore(
    (state) => state.closedTabsBySession
  );
  const currentSessionId = useTabHistoryStore(
    (state) => state.currentSessionId
  );
  const removeClosedTab = useTabHistoryStore((state) => state.removeClosedTab);

  // Keyboard navigation state
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Get closed tabs for current session
  const closedTabs = useMemo(() => {
    if (!currentSessionId) return [];
    return closedTabsBySession[currentSessionId] || [];
  }, [closedTabsBySession, currentSessionId]);

  // Convert tabs to unified format
  const allItems = useMemo(() => {
    const openItems = tabs
      .filter((tab) => tab.type !== 'start-page') // Exclude start page from list
      .map(tabToRecentItem);
    const closedItems = closedTabs.map(historyEntryToRecentItem);
    return sortTabItems([...openItems, ...closedItems]);
  }, [tabs, closedTabs]);

  // Filter by search query
  const filteredItems = useMemo(() => {
    return filterTabItems(allItems, searchQuery);
  }, [allItems, searchQuery]);

  // Reset selected index when filtered items change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [filteredItems.length, searchQuery]);

  // Handlers
  const handleToggleExpanded = useCallback(() => {
    onExpandedChange(!isExpanded);
  }, [isExpanded, onExpandedChange]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchQueryChange(e.target.value);
    },
    [onSearchQueryChange]
  );

  const reopenTab = useCallback(
    (entry: TabHistoryEntry) => {
      if (entry.type === 'query') {
        addTab({
          title: entry.title,
          customTitle: entry.customTitle,
          type: 'query',
          connectionInfo: entry.connectionInfo,
          content: tabHistoryEntryToQueryContent(entry),
        });
      } else if (entry.type === 'table-explorer') {
        addTab({
          title: entry.title,
          customTitle: entry.customTitle,
          type: 'table-explorer',
          connectionInfo: entry.connectionInfo,
          content: {
            tableName: entry.tableName || '',
            schema: entry.schema || 'dbo',
            database: entry.database || '',
          },
        });
      } else if (entry.type === 'plan') {
        addTab({
          title: entry.title,
          customTitle: entry.customTitle,
          type: 'plan',
          content: {
            view: entry.planView ?? 'single',
            filePath: entry.filePath,
            baselineFilePath: entry.baselineFilePath,
            compareFilePath: entry.compareFilePath,
          },
        });
      }

      // Remove from closed tabs
      removeClosedTab(entry.id);
    },
    [addTab, removeClosedTab]
  );

  const handleItemClick = useCallback(
    (item: RecentTabItem) => {
      if (item.isOpen && item.originalTab) {
        // Activate existing tab
        setActiveTab(item.originalTab.id);
      } else if (!item.isOpen && item.originalEntry) {
        // Reopen closed tab
        reopenTab(item.originalEntry);
      }
    },
    [setActiveTab, reopenTab]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (filteredItems.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < filteredItems.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < filteredItems.length) {
            handleItemClick(filteredItems[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onSearchQueryChange('');
          searchInputRef.current?.blur();
          break;
      }
    },
    [filteredItems, selectedIndex, handleItemClick, onSearchQueryChange]
  );

  // Stats for header badge
  const openTabCount = tabs.filter((t) => t.type !== 'start-page').length;
  const closedTabCount = closedTabs.length;
  const totalCount = openTabCount + closedTabCount;

  // Auto-collapse when there are no items
  useLayoutEffect(() => {
    if (totalCount === 0 && isExpanded) {
      onExpandedChange(false);
    }
  }, [totalCount, isExpanded, onExpandedChange]);

  return (
    <div
      className="rounded-lg border bg-surface-raised shadow-sm"
      data-testid="recent-tabs-section"
    >
      {/* Section Header */}
      <Button
        variant="ghost"
        className={cn(
          'w-full flex items-center justify-between px-4 py-3 h-auto',
          isExpanded && 'rounded-b-none'
        )}
        onClick={handleToggleExpanded}
        data-testid="recent-tabs-section-toggle"
      >
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4" />
          <span className="text-sm font-medium">Recent Tabs</span>
          <span className="text-xs text-muted-foreground tabular-nums">
            ({openTabCount} open
            {closedTabCount > 0 && `, ${closedTabCount} closed`})
          </span>
        </div>
        <ChevronDown
          className={cn('h-4 w-4 transition-transform duration-200', !isExpanded && '-rotate-90')}
        />
      </Button>

      {/* Section Content */}
      <AnimatedCollapse isOpen={isExpanded}>
        <div className="p-4 pt-2" data-testid="recent-tabs-section-content">
          {/* Search Input */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Search tabs..."
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              className="pl-9"
              data-testid="recent-tabs-search-input"
            />
          </div>

          {/* Tab List */}
          <div
            ref={listRef}
            role="listbox"
            aria-label="Recent tabs"
            className="max-h-[300px] overflow-y-auto rounded-md border border-border/50"
            onKeyDown={handleKeyDown}
            data-testid="recent-tabs-list"
          >
            {filteredItems.length > 0 ? (
              filteredItems.map((item, index) => (
                <TabListItem
                  key={item.id}
                  item={item}
                  isSelected={index === selectedIndex}
                  onClick={() => handleItemClick(item)}
                />
              ))
            ) : searchQuery ? (
              <div
                className="text-center py-8 text-muted-foreground"
                data-testid="recent-tabs-no-results"
              >
                <Search className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No tabs matching "{searchQuery}"</p>
                <p className="text-xs mt-1">Try a different search term</p>
              </div>
            ) : (
              <EmptyState
                icon={Layers}
                title="No recent tabs"
                description="Your open and closed tabs will appear here"
                testId="recent-tabs-empty-state"
              />
            )}
          </div>
        </div>
      </AnimatedCollapse>
    </div>
  );
}
